import type { User, UserStatus } from '@repo-level/core-front';
import type { BonanzaPromoApi } from 'api/cms/bonanzaPromo';
import type { PlayerBonus } from 'api/common';
import moment from 'moment';
import { CurrencyCode } from './currency';
import { isPromoAvailableDate } from './isDateRange';
import {
  bonusRestrictedGroups,
  groupMember,
  someGroupMember,
  USER_GROUP,
} from './user';

export enum BonanzaStatus {
  LEVEL0 = '0%',
  LEVEL1 = '25%',
  LEVEL2 = '50%',
  LEVEL3 = '75%',
  LEVEL4 = '100%',
  // tech status, not a user group
  BONUS_ISSUED = 'Bonus_Issued',
  PROMO_LOCKED = 'Used_Bonus',
  UNLOGGED = 'UNLOGGED',
}

export const BONANZA_STORAGE = 'bonanza';

const extractStatusPercent = (statusId: string): string =>
  statusId.split('_').slice(-1)[0];

const getHighestBonusGroup = (bonusGroups: UserStatus[]): UserStatus =>
  bonusGroups.reduce((max, current) =>
    Number.parseInt(extractStatusPercent(current.id), 10) >
    Number.parseInt(extractStatusPercent(max.id), 10)
      ? current
      : max
  );

export const getUserBonanzaGroup = (
  promoPrefix: string,
  statuses?: UserStatus[]
): BonanzaStatus => {
  if (!statuses) return BonanzaStatus.UNLOGGED;

  const bonusGroups = statuses.filter(
    ({ id }) => id.includes(promoPrefix) && id.includes('%')
  );

  if (!bonusGroups.length) return BonanzaStatus.LEVEL0;

  const bonusGroupPercent = extractStatusPercent(
    getHighestBonusGroup(bonusGroups).id
  ) as BonanzaStatus;

  const isLevel4 = bonusGroupPercent === BonanzaStatus.LEVEL4;
  const isHitTheBonanzaStorage = localStorage.getItem(BONANZA_STORAGE);

  if (isLevel4 && isHitTheBonanzaStorage) return BonanzaStatus.PROMO_LOCKED;
  if (isLevel4 && !isHitTheBonanzaStorage) return BonanzaStatus.BONUS_ISSUED;
  if (!isLevel4 && isHitTheBonanzaStorage)
    localStorage.removeItem(BONANZA_STORAGE);

  return bonusGroupPercent;
};

export const bonanzaPromoProgressPercent: Record<BonanzaStatus, number> = {
  [BonanzaStatus.UNLOGGED]: 0,
  [BonanzaStatus.LEVEL0]: 0,
  [BonanzaStatus.LEVEL1]: 25,
  [BonanzaStatus.LEVEL2]: 50,
  [BonanzaStatus.LEVEL3]: 75,
  [BonanzaStatus.LEVEL4]: 100,
  [BonanzaStatus.BONUS_ISSUED]: 100,
  [BonanzaStatus.PROMO_LOCKED]: 100,
};

const bonanzaSupportedCurrencies: string[] = [
  CurrencyCode.AUD,
  CurrencyCode.CAD,
  CurrencyCode.NZD,
  CurrencyCode.NOK,
  CurrencyCode.EUR,
  CurrencyCode.USD,
];

const bonanzaRewardMap: Record<
  string,
  Partial<Record<CurrencyCode, number>>
> = {
  [USER_GROUP.Bonanza_GROUP_A]: {
    [CurrencyCode.AUD]: 8,
    [CurrencyCode.CAD]: 8,
    [CurrencyCode.NZD]: 10,
    [CurrencyCode.NOK]: 60,
    [CurrencyCode.EUR]: 5,
    [CurrencyCode.USD]: 5,
  },
  [USER_GROUP.Bonanza_GROUP_B]: {
    [CurrencyCode.AUD]: 15,
    [CurrencyCode.CAD]: 15,
    [CurrencyCode.NZD]: 15,
    [CurrencyCode.NOK]: 90,
    [CurrencyCode.EUR]: 8,
    [CurrencyCode.USD]: 8,
  },
  [USER_GROUP.Bonanza_GROUP_C]: {
    [CurrencyCode.AUD]: 25,
    [CurrencyCode.CAD]: 25,
    [CurrencyCode.NZD]: 30,
    [CurrencyCode.NOK]: 170,
    [CurrencyCode.EUR]: 15,
    [CurrencyCode.USD]: 15,
  },
  [USER_GROUP.Bonanza_GROUP_D]: {
    [CurrencyCode.AUD]: 40,
    [CurrencyCode.CAD]: 40,
    [CurrencyCode.NZD]: 50,
    [CurrencyCode.NOK]: 280,
    [CurrencyCode.EUR]: 25,
    [CurrencyCode.USD]: 25,
  },
  [USER_GROUP.Bonanza_GROUP_E]: {
    [CurrencyCode.AUD]: 130,
    [CurrencyCode.CAD]: 130,
    [CurrencyCode.NZD]: 160,
    [CurrencyCode.NOK]: 900,
    [CurrencyCode.EUR]: 80,
    [CurrencyCode.USD]: 80,
  },
};

const bonanzaGroups = [
  USER_GROUP.Bonanza_GROUP_A,
  USER_GROUP.Bonanza_GROUP_B,
  USER_GROUP.Bonanza_GROUP_C,
  USER_GROUP.Bonanza_GROUP_D,
  USER_GROUP.Bonanza_GROUP_E,
];

const findBonanzaGroup = (user?: User) => {
  if (!user?.statuses) return;

  return user.statuses
    .map(({ id }) => id.toLowerCase())
    .find((id) => bonanzaGroups.includes(id as USER_GROUP));
};

export const getUserReward = (user?: User) => {
  const userCurrency = user?.currency ?? CurrencyCode.USD;
  const currency = bonanzaSupportedCurrencies.includes(userCurrency)
    ? (userCurrency as CurrencyCode)
    : CurrencyCode.USD;

  const userGroupId = findBonanzaGroup(user) ?? USER_GROUP.Bonanza_GROUP_A;

  const reward = bonanzaRewardMap[userGroupId]?.[currency] ?? 0;

  return { reward, currency };
};

export const getBonanzaBonus = (bonuses: PlayerBonus[], promoPrefix: string) =>
  bonuses.find(({ title }) => title.includes(promoPrefix));

export const BONANZA_ITERATION_DURATION_DAYS = 3;

const BONANZA_FULL_DATE_FORMAT: Partial<Record<string, string>> = {
  de: 'ddd (D MMM)',
};

export const getCurrentBonanzaIterationDays = (
  startDate: string,
  endDate: string,
  locale: string
): {
  startDay: string;
  endDay: string;
  endDayFull: string;
  nextStartDay: string;
  isLastIteration: boolean;
} => {
  const promoStart = moment.utc(startDate);
  const promoEnd = moment.utc(endDate);
  const today = moment.utc();
  const daysSinceStart = Math.max(0, today.diff(promoStart, 'days'));
  const currentIteration = Math.floor(
    daysSinceStart / BONANZA_ITERATION_DURATION_DAYS
  );
  const totalIterations = Math.ceil(
    promoEnd.diff(promoStart, 'days') / BONANZA_ITERATION_DURATION_DAYS
  );
  const isLastIteration = currentIteration + 1 >= totalIterations;

  const momentLocale = locale.split('-')[0];
  const fullDateFormat =
    BONANZA_FULL_DATE_FORMAT[momentLocale] ?? 'ddd (D MMM)';

  const currentIterationStart = promoStart
    .clone()
    .add(currentIteration * BONANZA_ITERATION_DURATION_DAYS, 'days');
  const currentIterationEnd = currentIterationStart
    .clone()
    .add(BONANZA_ITERATION_DURATION_DAYS - 1, 'days');
  const nextIterationStart = currentIterationStart
    .clone()
    .add(BONANZA_ITERATION_DURATION_DAYS, 'days');

  const stripDots = (str: string) => str.replace(/\./g, '');

  return {
    startDay: stripDots(
      currentIterationStart.locale(momentLocale).format(fullDateFormat)
    ),
    endDay: stripDots(
      currentIterationEnd.locale(momentLocale).format(fullDateFormat)
    ),
    endDayFull: stripDots(
      currentIterationEnd.locale(momentLocale).format(fullDateFormat)
    ),
    nextStartDay: nextIterationStart.locale(momentLocale).format('dddd'),
    isLastIteration,
  };
};

export const checkIsBonanzaPromoAvailable = (
  data: BonanzaPromoApi | null,
  user?: User
) => {
  if (data && data.start && data.end) {
    const end = moment.utc(data.end);
    const now = moment.utc();
    const diffDays = end.diff(now, 'days');
    const isLastWeek = diffDays <= 7;
    const isPromoLocked = groupMember(BonanzaStatus.PROMO_LOCKED)(user);

    return (
      !someGroupMember(bonusRestrictedGroups)(user) &&
      isPromoAvailableDate(data.start, data.end) &&
      !(isPromoLocked && isLastWeek)
    );
  }
  return false;
};
